import type { JssStyle } from 'jss';
import type { ThemedBaseButtonProp } from './base-button.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import type { ThemeButtonHoverLegacy as ThemeButtonHover } from '@sites/entity-core';
import { EButtonHoverType } from '@common/enums';
import type { AdvancedRadiusSettings } from '@components/sp-advanced-radius-settings/type/sp-advanced-radius-settings.type';
import type { AdvancedBorderSettings } from '@components/sp-advanced-border-settings/type/advanced-border-settings.type';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

export function getBaseButtonStyles(buttonBase: Partial<ThemedBaseButtonProp> = {}): JssStyle {
    // TODO: remove all nullish coalescing operators
    return {
        color: themeColorCss(buttonBase?.color?.[0]),
        background: themeColorCss(buttonBase?.background?.[0]),
        borderColor: themeColorCss(buttonBase?.borderColor?.[0]),
        borderWidth: buttonBase?.borderWidth + 'px',
        borderRadius: buttonBase?.borderRadius + 'px',
        borderStyle: buttonBase?.borderStyle,
        '&:hover': {
            color: themeColorCss(buttonBase?.color?.[0]),
        },
    };
}

export type GetBaseButtonDataParams = {
    buttonBase: ThemedBaseButtonProp;
    buttonHover: ThemeButtonHover;
    advancedRadius?: AdvancedRadiusSettings;
    advancedBorderSettings?: AdvancedBorderSettings;
    isShadow?: boolean;
    shadowSettings?: ShadowSettings;
};

export function getBaseButtonData({
    buttonBase,
    buttonHover,
    advancedRadius,
    advancedBorderSettings,
    isShadow,
    shadowSettings,
}: GetBaseButtonDataParams): JssStyle {
    let hoverStyles: JssStyle;

    let borderRadius: JssStyle;

    switch (buttonHover?.hoverType) {
        case EButtonHoverType.opacity:
            hoverStyles = {
                opacity: buttonHover.opacity * 0.01,
                color: themeColorCss(buttonBase?.color?.[0]),
            };
            break;
        case EButtonHoverType.shadow:
            hoverStyles = {
                boxShadow: buttonHover.shadow,
                color: themeColorCss(buttonBase?.color?.[0]),
            };
            break;
        case EButtonHoverType.color:
            hoverStyles = {
                color: themeColorCss(buttonHover?.color?.[0]),
                background: themeColorCss(buttonHover?.background?.[0]),
                borderColor: themeColorCss(buttonHover?.borderColor?.[0]),
            };
            break;
        default:
            break;
    }

    if (advancedRadius) {
        borderRadius = {
            borderTopLeftRadius:
                (advancedRadius?.borderTopLeftRadius || advancedRadius?.borderTopLeftRadius === 0
                    ? advancedRadius?.borderTopLeftRadius
                    : buttonBase?.borderRadius) + 'px',
            borderTopRightRadius:
                (advancedRadius?.borderTopRightRadius || advancedRadius?.borderTopRightRadius === 0
                    ? advancedRadius?.borderTopRightRadius
                    : buttonBase?.borderRadius) + 'px',
            borderBottomLeftRadius:
                (advancedRadius?.borderBottomLeftRadius || advancedRadius?.borderBottomLeftRadius === 0
                    ? advancedRadius?.borderBottomLeftRadius
                    : buttonBase?.borderRadius) + 'px',
            borderBottomRightRadius:
                (advancedRadius?.borderBottomRightRadius || advancedRadius?.borderBottomRightRadius === 0
                    ? advancedRadius?.borderBottomRightRadius
                    : buttonBase?.borderRadius) + 'px',
        };
    } else {
        borderRadius = {
            borderRadius: buttonBase?.borderRadius + 'px',
        };
    }

    let borderStyles: JssStyle;

    if (advancedBorderSettings) {
        const abs = advancedBorderSettings;

        if (abs.isIndependentBorders) {
            borderStyles = {
                borderTop: `${abs.borderTopWidth}px ${abs.borderTopStyle} ${abs.borderTopColor}`,
                borderRight: `${abs.borderRightWidth}px ${abs.borderRightStyle} ${abs.borderRightColor}`,
                borderBottom: `${abs.borderBottomWidth}px ${abs.borderBottomStyle} ${abs.borderBottomColor}`,
                borderLeft: `${abs.borderLeftWidth}px ${abs.borderLeftStyle} ${abs.borderLeftColor}`,
            };
        } else {
            borderStyles = {
                border: `${abs.borderTopWidth}px ${abs.borderTopStyle} ${abs.borderTopColor}`,
            };
        }
    } else {
        borderStyles = {
            borderColor: themeColorCss(buttonBase?.borderColor?.[0]),
            borderWidth: buttonBase?.borderWidth + 'px',
            borderStyle: buttonBase?.borderStyle,
        };
    }

    const shadowStyle: JssStyle = {};

    if (isShadow) {
        const s = shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        shadowStyle.boxShadow = `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`;
    }

    return {
        color: themeColorCss(buttonBase?.color?.[0]),
        background: themeColorCss(buttonBase?.background?.[0]),
        ...borderStyles,
        ...borderRadius,
        ...shadowStyle,
        '&:hover': {
            ...hoverStyles,
        },
    };
}

export function getBaseButtonIconStyles(fontSize: number = 16): JssStyle {
    return {
        '& .icon': {
            width: 'var(--button-font-size)',
            height: 'var(--button-font-size)',
            '& svg': {
                width: 'var(--button-font-size)',
                height: 'var(--button-font-size)',
            },
        },
    };
}
